import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../shared/components/Layout";
import InfoDisplay from "@/components/InfoDisplay";
import { AddressFieldBox } from "@/components/AddressField";
import { PhoneFieldBox } from "@/components/PhoneField";
import { formatDate, formatMonth } from "@/utils/format";
import type { FlatRateAgreement, Applicant, Consumer, ChangePlanRequest, ChangePlanRequestParams } from "../types";
import type OccupationLabels from "../../shared/types/OccupationLabels";
import type SexLabels from "../../shared/types/SexLabels";
import getChangePlanRequest from "../api/getChangePlanRequest";
import createChangePlanRequest from "../api/createChangePlanRequest";
import clearLocalStorage from "@/utils/clearLocalStorage";

const ChangePlanRequestConfirm: React.FC = () => {
  const displayPlan = (plan: string, price: string): string => {
    if(plan === 'both') return `両眼プラン(${price}円/月)`
    if(plan === 'right') return `片眼プラン(右眼)(${price}円/月)`
    if(plan === 'left') return `片眼プラン(左眼)(${price}円/月)`
    return '';
  }

  const navigate = useNavigate();

  const [customer, setCustomer] = useState({
    custCode: "",
    email: "",
  });

  const [flatRateAgreement, setFlatRateAgreement] = useState<FlatRateAgreement>();
  const [changePlanRequest, setChangePlanRequest] = useState<ChangePlanRequest>({
    id: "",
    beforePlan: "",
    beforePrice: "",
    afterPlan: "",
    afterPrice: "",
    changeMonth: ""
  });
  const [applicant, setApplicant] = useState<Applicant>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    sex: "",
    stateId: "",
    zipcode: "",
  });
  const [consumer, setConsumer] = useState<Consumer>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    relationship: "",
    sex: "",
    stateId: "",
    zipcode: "",
    isSameApplicant: true,
    isSameAddress: true,
    isSamePhone: true
  });
  
  const [states, setStates] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getChangePlanRequest();
        setCustomer(response.data.customer);
        setFlatRateAgreement(response.data.flatRateAgreement);
        setChangePlanRequest(response.data.changePlanRequest);
        setApplicant(response.data.applicant);
        setConsumer(prevConsumer => ({...prevConsumer, ...response.data.consumer}));
        setStates(response.data.states);
      } catch (error) {
        console.error("Error fetching change plan request data:", error);
      }
    }

    fetchData();
  }, []);

  const applicantAddressData = {
    zipcode: {
      label: "郵便番号",
      value: applicant.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: applicant.stateId ? applicant.stateId.toString() : "",
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: applicant.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: applicant.address2,
      onChange: undefined
    },
  }

  const consumerAddressData = {
    zipcode: {
      label: "郵便番号",
      value: consumer.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: consumer.stateId ? consumer.stateId.toString() : "",
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: consumer.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: consumer.address2,
      onChange: undefined
    },
  }

  const OccupationLabels: OccupationLabels = {
    employee: "会社員・公務員",
    independent: "自営",
    housewife: "主婦",
    student: "学生",
    other: "その他"
  }

  const SexLabels: SexLabels = {
    male: "男性",
    female: "女性",
    unknown: "不明",
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: ChangePlanRequestParams = {
      id: changePlanRequest.id
    }

    try {
      const res = await createChangePlanRequest(params);
      if(res.status === 201) {
        clearLocalStorage();
        navigate("/customer/flat_rates/change_plan_request/complete");
      } else if(res.status !== 201) {
        window.scrollTo(0, 0);
        console.error(`status: ${res.status}, message: ${res.statusText}`);
      }
    } catch (err: any) {
      console.log('An unexpected error occurred:', err.message);
    }
  }

  return (
    <Layout>
      <main id="page-user_index" className="wrapper">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">契約内容変更の確認・確定</h1>
            <div className="contact-box">
              <div className="contact-box__left">
                <form onSubmit={handleSubmit}>
                  <div className="contact-box__form js-item" id="id-01">
                    <h2 className="c-ttl__02">お客様情報</h2>
                    <InfoDisplay label={"お客様ID"} value={customer.custCode} />
                    <div className="contact-box__item">
                      <label className="contact-box__ttl">
                        <span>ご契約プラン</span>
                      </label>
                      <div className="contact-box__ct">
                        <p className="contact-box__desc mbpc-5 mbsp-5">{displayPlan(changePlanRequest?.beforePlan ?? '', changePlanRequest?.beforePrice?.toString() ?? '')}</p> 
                        <p className="contact-box__desc">
                          {`→ ${displayPlan(changePlanRequest?.afterPlan ?? '', changePlanRequest?.afterPrice.toString() ?? '')}}`}
                          <br />
                          {`プラン変更月: ${formatMonth(changePlanRequest?.changeMonth ?? '')}`}
                        </p>
                      </div>
                    </div>
                    <InfoDisplay label={"サービス利用開始日"} value={formatDate(flatRateAgreement?.startDate)} />
                    <InfoDisplay label="サービス利用開始月" value={formatMonth(flatRateAgreement?.startMonth ?? '')} />
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                    <h2 className="c-ttl__02">申込者情報</h2>
                    <InfoDisplay label="申込者名(カナ)" value={applicant.nameKana} />
                    <InfoDisplay label="申込者名(漢字)" value={applicant.name} />
                    <InfoDisplay label="申込者性別" value={applicant.sex ? SexLabels[applicant.sex] : ""} />
                    <InfoDisplay label="申込者生年月日" value={formatDate(applicant.birthday)} />
                    <AddressFieldBox
                      label="申込者住所"
                      addressData={applicantAddressData}
                      required={false}
                      states={states}
                      inputAddressData={undefined}
                    />
                    <PhoneFieldBox
                      label="申込者電話番号"
                      phoneData={{
                        homePhone: {label: "自宅", value: applicant.homePhone, onChange: undefined},
                        mobilePhone: {label: "携帯", value: applicant.mobilePhone, onChange: undefined},
                      }}
                      required={false}
                    />
                    <InfoDisplay label="申込者ご職業" value={applicant.occupation ? OccupationLabels[applicant.occupation] : ""} />
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                    <h2 className="c-ttl__02">レンズ使用者情報</h2>
                    <InfoDisplay label="使用者について" value={consumer.isSameApplicant ? "申込者と同一" : "申込者と異なる"} />
                    { !consumer.isSameApplicant &&
                      <>
                        <InfoDisplay label="使用者名(カナ)" value={consumer.nameKana} />
                        <InfoDisplay label="使用者名(漢字)" value={consumer.name} />
                        <InfoDisplay label="申込者との関係" value={consumer.relationship} />
                        <InfoDisplay label="使用者性別" value={consumer.sex ? SexLabels[consumer.sex] : ""} />
                        <InfoDisplay label="使用者生年月日" value={formatDate(consumer.birthday)} />
                        { consumer.isSameAddress ?
                          <InfoDisplay label="使用者住所" value={"申込者と同一"} />
                        :
                          <AddressFieldBox
                            label="使用者住所"
                            addressData={consumerAddressData}
                            required={false}
                            states={states}
                            inputAddressData={undefined}
                          />
                        }

                        { consumer.isSamePhone ?
                          <InfoDisplay label="使用者電話番号" value={"申込者と同一"} />
                        :
                          <PhoneFieldBox
                            label="使用者電話番号"
                            phoneData={{
                              homePhone: {label: "自宅", value: consumer.homePhone, onChange: undefined},
                              mobilePhone: {label: "携帯", value: consumer.mobilePhone, onChange: undefined},
                            }}
                            required={false}
                          />
                        }
                        <InfoDisplay label="申込者ご職業" value={consumer.occupation ? OccupationLabels[consumer.occupation] : ""} />
                      </>
                    }
                  </div>

                  <button type="submit" className="c-btn__01">確定</button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
};

export default ChangePlanRequestConfirm;